// Used for replacing prop templates in code examples
import type { ComponentPreviewProps, ReactPreviewAdditionalComponentProps } from '@/app/ui/components/molecules/component-preview/component-preview';

// TODO move react content generation to a separate file, use nice design pattern to handle it
export const generateReactContent = (props: ComponentPreviewProps['react']) => {
  const BASE_COMPONENTS = [
    'Accordion',
    'Alert',
    'Avatar',
    'Badge',
    'Breadcrumbs',
    'Button',
    'Checkbox',
    'Divider',
    'Input',
    'Radio',
    'Rate',
    'Select',
    'Spinner',
    'Switch',
    'Tabs',
    'Tag',
    'Textarea',
    'Toast',
    'Tooltip',
  ].sort();

  const content = typeof props === 'string' ? props : props.content;
  const additionalComponents = typeof props === 'string' ? [] : props.additionalComponents ?? [];
  const componentsImports: string[] = BASE_COMPONENTS.reduce<string[]>(
    (results, component) =>
      content.includes(`<${component}`) || additionalComponents.some(({ content }) => content.includes(`<${component}`)) ? [...results, component] : results,
    []
  );

  const additionalComponentsIconsImports = additionalComponents.flatMap(({ content }) => [...content.matchAll(/(?<=<)[A-Za-z]+Icon/g)]).map(match => match[0]);

  const iconsImports: string[] = [
    ...new Set([...[...content.matchAll(/(?<=<)[A-Za-z]+Icon/g)].map(match => match[0]), ...additionalComponentsIconsImports]),
  ].sort();

  return (
    (componentsImports.length > 0 ? `${`import { ${componentsImports.join(', ')} } from '@/components';`}${componentsImports.length > 0 ? '\n' : ''}` : '') +
    (iconsImports.length > 0 ? `${`import { ${iconsImports.join(', ')} } from '@/icons';`}${iconsImports.length > 0 ? '\n' : ''}` : '') +
    `${componentsImports.length > 0 || iconsImports.length > 0 ? '\n' : ''}` +
    (additionalComponents.length > 0
      ? additionalComponents.map(props => (props.raw ? props.content : generateReactComponent(props))).join('\n\n') + '\n\n'
      : '') +
    generateReactComponent({ content, name: 'Preview', defaultComponent: true })
  );
};

const generateReactComponent = ({ content, name, props = [], defaultComponent }: ReactPreviewAdditionalComponentProps) => {
  const withProps = replacePropsTemplateExpressions({ content, props });
  const withNewlines = replaceNewLinesTemplateExpressions(withProps, 'react');
  const withComments = replaceCommentsTemplateExpressions(withNewlines, 'react');
  const normalizedContent = replaceTemplateStringProps(withComments);

  return `${defaultComponent ? 'export default ' : ''}function ${name}(${
    props.length > 0 ? `{ ${props?.map(prop => (typeof prop === 'string' ? prop : prop.name)).join(', ')} }` : ''
  }) {
  return (
${normalizedContent.replace(/^(?!>)/gm, '    ')}
  );
};`;
};

export const PROP_TEMPLATE_CHAR = '§';
export const createPropTemplateExpression = (name: string) => `${PROP_TEMPLATE_CHAR}${name}${PROP_TEMPLATE_CHAR}`;
export const generateComment = (comment: string, { htmlOnly = false, reactOnly = false }: { htmlOnly?: boolean; reactOnly?: boolean } = {}) => (
  <div className='hidden' data-html-only={htmlOnly} data-react-only={reactOnly} data-comment={comment}></div>
);
export const generateHtmlNewLine = () => <div className='hidden' data-newline></div>;

export const replacePropsTemplateExpressions = ({ content, props = [] }: Pick<ReactPreviewAdditionalComponentProps, 'content' | 'props'>): string => {
  // adding JSX brackets to props
  let newContent = content;
  for (const prop of props) {
    const propPattern = createPropTemplateExpression(typeof prop === 'string' ? prop : prop.name);
    if (typeof prop === 'string') {
      newContent = newContent.replace(
        RegExp(`(${propPattern}|(")${propPattern}("))`, 'g'),
        substring => `{${substring.replace(new RegExp(`[${PROP_TEMPLATE_CHAR}"]`, 'g'), '')}}`
      );
    } else {
      if (Array.isArray(prop.replacer)) {
        for (const replacer of prop.replacer) {
          newContent = newContent.replace(RegExp(`(${propPattern}|(")${propPattern}("))`, ''), () => replacer);
        }
      } else {
        newContent = newContent.replace(RegExp(`(${propPattern}|(")${propPattern}("))`, 'g'), () => prop.replacer as string);
      }
    }
  }

  return newContent;
};

export const replaceCommentsTemplateExpressions = (content: string, type: 'html' | 'react'): string => {
  if (type === 'html') {
    return content.replace(/<div\s+class="hidden"\s+data-html-only="[^"]*"\s+data-react-only="[^"]*"\s+data-comment="[^"]*">\s*<\/div>/gm, substring => {
      if (substring.includes('data-react-only="true"')) {
        return '';
      }

      return `<!-- ${substring.replace(/((<div\s+class="hidden".*data-comment=")|(">\s*<\/div>))/g, '')} -->`;
    });
  }

  return content
    .replace(/<div\s+className="hidden"\s*.*\s*data-comment="[^"]+"\s*\/>/gm, substring => {
      if (substring.includes('data-html-only')) {
        return '';
      }

      return `{/* ${substring.replace(/((<div\s+className="hidden"\s*.*\s*data-comment=")|("\s+\/>))/gm, '')} */}`;
    })
    .replace(/\n^\s*$/gm, '');
};

export const replaceNewLinesTemplateExpressions = (content: string, type: 'html' | 'react') => {
  if (type === 'html') {
    return content.replace(/<div\s+class="hidden"\s+data-newline="true"><\/div>/gm, substring =>
      substring.replace(/<div\s+class="hidden"\s+data-newline="true">\s*<\/div>/gm, '')
    );
  }

  return content.replace(/<div\s+className="hidden"\s+data-newline\s*\/>/gm, substring =>
    substring.replace(/<div\s+className="hidden"\s+data-newline\s*\/>/gm, '')
  );
};

export const replaceTemplateStringProps = (content: string) => {
  return content.replace(/className=".*{.*}.*"/gm, substring => substring.replace('{', '${').replace('="', '={`').replace('"', '`}'));
};

// TODO replacer for specific html tags e.g. h2 -> h1 etc.
